import { ReactElement, ReactNode } from "react";
import styled from "styled-components";
import { AnswerValueType } from "../../../types/Answer";
import {
  CheckboxQuestionType,
  DateQuestionType,
  DropdownQuestionType,
  HourQuestionType,
  InformationQuestionType,
  NumberQuestionType,
  QuestionType,
  QuestionTypeEnum,
  RadioQuestionType,
  SliderQuestionType,
  TextQuestionType,
} from "../../../types/Question";
import { RichContentData } from "../../../types/richContent";
import ContentRenderer from "../../organisms/ContentRenderer/ContentRenderer";
import { toPlainText } from "../../../helper/richContentText";
import { useConfig } from "../../../contexts/ConfigContext";
import Icon from "../../atoms/Icon";
import { volumeHighOutline } from "ionicons/icons";
import AnswerCheckbox from "./Answers/AnswerCheckbox";
import AnswerDate from "./Answers/AnswerDate";
import AnswerDropdown from "./Answers/AnswerDropdown";
import AnswerHour from "./Answers/AnswerHour";
import AnswerNumber from "./Answers/AnswerNumber";
import AnswerRadio from "./Answers/AnswerRadio";
import AnswerSlider from "./Answers/AnswerSlider";
import AnswerText from "./Answers/AnswerText";
import QuestionDisplay from "./QuestionDisplay/QuestionDisplay";
import { useTranslation } from "react-i18next";

interface QuestionProps {
  question: QuestionType;
  answer: AnswerValueType;
  onAnswerChange: (answer: AnswerValueType) => void;
  ruleMessage?: string;
  readText?: (text: string) => void;
  cardMargin?: string;
  // Pressing Enter in a single-line text / number field triggers "Next".
  onEnterNext?: () => void;
}

const componentByQuestionType: Record<
  QuestionTypeEnum,
  (props: QuestionProps) => ReactElement
> = {
  CHECKBOX: ({
    question,
    answer,
    onAnswerChange,
  }: QuestionProps): ReactElement => (
    <AnswerCheckbox
      question={question as CheckboxQuestionType}
      value={answer as number[]}
      onChange={onAnswerChange}
    />
  ),
  RADIOBUTTON: ({
    question,
    answer,
    onAnswerChange,
  }: QuestionProps): ReactElement => (
    <AnswerRadio
      question={question as RadioQuestionType}
      value={
        typeof answer === "string"
          ? Number(answer)
          : typeof answer === "number"
          ? answer
          : null
      }
      onChange={(v: number) => onAnswerChange(v)}
    />
  ),
  DROPDOWN: ({
    question,
    answer,
    onAnswerChange,
  }: QuestionProps): ReactElement => (
    <AnswerDropdown
      question={question as DropdownQuestionType}
      value={
        typeof answer === "string"
          ? Number(answer)
          : typeof answer === "number"
          ? answer
          : null
      }
      onChange={(v: number) => onAnswerChange(v)}
    />
  ),
  TEXT: ({ question, answer, onAnswerChange, onEnterNext }: QuestionProps): ReactElement => (
    <AnswerText
      question={question as TextQuestionType}
      value={answer as string}
      onChange={onAnswerChange}
      onEnter={onEnterNext}
    />
  ),
  NUMBER: ({
    question,
    answer,
    onAnswerChange,
    onEnterNext,
  }: QuestionProps): ReactElement => (
    <AnswerNumber
      question={question as NumberQuestionType}
      value={answer as number}
      onChange={onAnswerChange}
      onEnter={onEnterNext}
    />
  ),
  DATE: ({ question, answer, onAnswerChange }: QuestionProps): ReactElement => (
    <AnswerDate
      question={question as DateQuestionType}
      value={answer as string}
      onChange={onAnswerChange}
    />
  ),
  SLIDER: ({
    question,
    answer,
    onAnswerChange,
  }: QuestionProps): ReactElement => (
    <AnswerSlider
      question={question as SliderQuestionType}
      value={answer as number}
      onChange={onAnswerChange}
    />
  ),
  HOUR: ({ question, answer, onAnswerChange }: QuestionProps): ReactElement => (
    <AnswerHour
      question={question as HourQuestionType}
      value={answer as string}
      onChange={onAnswerChange}
    />
  ),
  INFORMATION: (props: QuestionProps): ReactElement => (
    <InformationPage {...props} />
  ),
};

const EMPTY_RICH_CONTENT: RichContentData = { blocks: [] };

// Full-page rich-content body for INFORMATION questions. Deliberately card-less: none
// of the QuestionDisplay chrome (title, mandatory star, constraint line) applies — the
// authored content IS the page. The inset vars are the ContentRenderer breakout
// contract so full-bleed hero blocks reach the edges (same values as DisplayContent).
const InformationPage = ({ question, readText }: QuestionProps): ReactElement => {
  const { selectedRole } = useConfig();
  const data =
    (question as InformationQuestionType).detail?.content?.content ?? EMPTY_RICH_CONTENT;
  const isVocalisationEnabled =
    selectedRole?.attributes?.is_vocalisation_enabled !== false;

  return (
    <StyledInformationPage>
      {isVocalisationEnabled && readText && (
        <InformationButtons>
          <InformationIcon
            icon={volumeHighOutline}
            onClick={(): void => readText(toPlainText(data))}
          />
        </InformationButtons>
      )}
      <div
        style={{
          padding: 20,
          ["--content-inset" as never]: "20px",
          ["--content-inset-top" as never]: "20px",
        }}
      >
        <ContentRenderer data={data} />
      </div>
    </StyledInformationPage>
  );
};

const Question = ({
  question,
  answer,
  onAnswerChange,
  ruleMessage,
  readText,
  cardMargin,
  onEnterNext,
}: QuestionProps): ReactElement => {
  const { type, id, title, description } = question;

  const AnswerComponent = componentByQuestionType[type];
  const { t } = useTranslation();

  // INFORMATION: full-page content, no QuestionDisplay card/chrome at all.
  if (type === QuestionTypeEnum.INFORMATION) {
    return (
      <StyledQuestion>
        <InformationPage
          key={id}
          question={question}
          answer={answer}
          onAnswerChange={onAnswerChange}
          readText={readText}
        />
      </StyledQuestion>
    );
  }

  const answerComp = (
    <AnswerComponent
      key={id}
      question={question}
      answer={answer}
      onAnswerChange={onAnswerChange}
      readText={readText}
      onEnterNext={onEnterNext}
    />
  );

  return (
    <StyledQuestion>
      <QuestionDisplay
        answerComp={answerComp}
        question={question}
        ruleMessage={ruleMessage}
        onReadQuestionClick={(): void =>
          readText?.(`${title || ""}. ${description || ""}`)
        }
        cardMargin={cardMargin}
      />
       {/* <Help>
        <p>{t("help_link_part1")} 
          <a href="https://etude-mentalo.fr/definitions-et-ressources/" target="_blank" style={{ color: 'inherit' }}>{t("help_link_part2")}</a>.
        </p>
      </Help> */}
    </StyledQuestion>
  );
};

const StyledQuestion = styled.div`
  ion-icon {
    color: ${(props) => props.theme.primary.main};
  }
`;

const StyledInformationPage = styled.div`
  background: white;
`;

const InformationButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  padding: 10px 20px 0;
`;

const InformationIcon = styled(Icon)`
  font-size: 26px;
  color: ${(props) => props.theme.primary.main};
  &:active {
    opacity: 0.5;
  }
`;

const Help = styled.div`
  margin: 5px 25px;
`;

export default Question;